import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  Table,
  TableColumn,
  SupportButton,
  StatusOK,
  StatusError,
  StatusRunning,
  EmptyState,
  Link,
  Progress,
} from '@backstage/core-components';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Button,
  Checkbox,
  Chip,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import AddIcon from '@material-ui/icons/Add';
import Skeleton from '@material-ui/lab/Skeleton';
import { spassuCodeApiRef } from '../../api';
import {
  Activity,
  ActivityFilterOptions,
  ActivityFilters,
  ActivityStatus,
  ActivityWorkPath,
  PHASE_LABELS,
  STATUS_LABELS,
} from '../../api/types';
import type { ActivityDeletionBatchView } from '@internal/spassu-code-contract';
import { useProjectRole } from '../../hooks/useProjectRole';
import { AdminDeletionPanel } from './AdminDeletionPanel';
import { ActivityFiltersPanel } from './ActivityFiltersPanel';
import { ControlPlaneCycleCell } from './ControlPlaneStateCells';
import {
  ActivityTitleCell,
  CreationCell,
  DestinationCell,
} from './ActivityTableCells';
import {
  activityConnectRouteRef,
  activityDetailRouteRef,
  activityMetricsRouteRef,
  controlPlaneCockpitRouteRef,
} from '../../routes';
import { ActivityJourneyLauncher } from '../../activity-creation';
import { WorkerStatusIndicator } from '../WorkerStatusIndicator';
import {
  formatUserEntityRef,
  getCatalogEntityDisplayName,
} from '../../utils/format';
import { normalizeRouteRefPath } from '../../utils/routes';

/** Opaque batch correlation id, per tab. Never a secret (ADR-015). */
const DELETION_BATCH_STORAGE_KEY = 'spassu-code.deletion-batch';
const DELETION_POLL_BASE_MS = 3_000;
const DELETION_POLL_MAX_MS = 30_000;

const useStyles = makeStyles(theme => ({
  chip: {
    marginRight: theme.spacing(0.5),
  },
  loadingSkeleton: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gap: theme.spacing(1),
  },
  actionInfo: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
    '& table': { tableLayout: 'fixed', width: '100%' },
    '& th, & td': { overflow: 'hidden' },
  },
}));

const StatusIndicator = ({
  status,
  errorMessage,
}: {
  status: ActivityStatus;
  errorMessage?: string | null;
}) => {
  switch (status) {
    case 'active':
      return <StatusRunning>{STATUS_LABELS.active}</StatusRunning>;
    case 'completed':
      return <StatusOK>{STATUS_LABELS.completed}</StatusOK>;
    case 'error': {
      const indicator = <StatusError>{STATUS_LABELS.error}</StatusError>;
      return errorMessage ? (
        <Tooltip title={errorMessage} arrow>
          <span>{indicator}</span>
        </Tooltip>
      ) : (
        indicator
      );
    }
    default:
      return <>{STATUS_LABELS[status]}</>;
  }
};

const PhaseChip = ({ phase }: { phase: string }) => {
  const label = PHASE_LABELS[phase as keyof typeof PHASE_LABELS] ?? phase;
  return <Chip size="small" label={label} variant="outlined" />;
};

const WORK_PATH_LABELS: Record<ActivityWorkPath, string> = {
  'frontier-web': 'Frontier Web',
  'cli-standalone': 'CLI Standalone',
  'control-plane': 'Control Plane',
};

const WorkPathChip = ({ activity }: { activity: Activity }) => {
  const workPath = activity.workPath ?? 'frontier-web';
  return (
    <Chip size="small" variant="outlined" label={WORK_PATH_LABELS[workPath]} />
  );
};

function isTerminalWorkPath(activity: Activity): boolean {
  return (
    activity.workPath === 'cli-standalone' ||
    activity.workPath === 'control-plane'
  );
}

export interface ActivityListPageProps {
  workPath?: ActivityWorkPath;
  portfolioTitle?: string;
  portfolioSubtitle?: string;
  supportText?: string;
  emptyDescription?: string;
  showWorkerStatus?: boolean;
  onCreate?: () => void;
}

export type CreationEntryState =
  | 'allowed'
  | 'absent'
  | 'pending'
  | 'unavailable';

/**
 * Um estado só governa todas as entradas de criação da página. `pending` e
 * `unavailable` são indeterminação, e indeterminação não libera criação
 * (fail-closed, ADR-002); `absent` é ausência de permissão, que oculta a
 * entrada sem erro.
 */
export function resolveCreationEntryState(input: {
  rbacError: boolean;
  rbacLoading: boolean;
  canCreateActivity: boolean;
}): CreationEntryState {
  if (input.rbacError) return 'unavailable';
  if (input.rbacLoading) return 'pending';
  return input.canCreateActivity ? 'allowed' : 'absent';
}

/**
 * Entrada de criação de atividade. Existe uma única implementação para que as
 * duas entradas da página — cabeçalho e estado vazio — não possam divergir, e
 * para que a distinção entre "você não pode" e "não foi possível verificar" seja
 * a mesma nas duas.
 *
 * - `allowed`: botão acionável.
 * - `absent`: sem permissão em projeto algum; a entrada não é renderizada, e o
 *   portfólio segue legível sem erro.
 * - `pending` e `unavailable`: indeterminação. A criação não é liberada e o
 *   motivo é dito — nunca liberação silenciosa (ADR-002).
 */
const CreationEntry = ({
  state,
  onCreate,
  onRetry,
}: {
  state: CreationEntryState;
  onCreate: () => void;
  onRetry: () => void;
}) => {
  if (state === 'absent') return null;

  const supportId = 'creation-entry-support';
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onCreate}
        disabled={state !== 'allowed'}
        aria-describedby={state === 'allowed' ? undefined : supportId}
      >
        Nova Atividade
      </Button>
      {state === 'pending' && (
        <Typography id={supportId} variant="caption" component="span">
          Confirmando permissões...
        </Typography>
      )}
      {state === 'unavailable' && (
        <Alert
          id={supportId}
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível confirmar suas permissões agora. A criação de
          atividades fica indisponível até a confirmação.
        </Alert>
      )}
    </>
  );
};

export const ActivityListPage = ({
  workPath,
  portfolioTitle = 'Frontier',
  portfolioSubtitle = 'Desenvolvimento assistido por IA',
  supportText = 'Plugin para desenvolvimento assistido por IA com pipeline de 7 fases.',
  emptyDescription = 'Crie uma nova atividade para começar a usar o Frontier.',
  showWorkerStatus = true,
  onCreate,
}: ActivityListPageProps = {}) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const activityDetailRoute = useRouteRef(activityDetailRouteRef);
  const activityConnectRoute = useRouteRef(activityConnectRouteRef);
  const activityMetricsRoute = useRouteRef(activityMetricsRouteRef);
  const controlPlaneCockpitRoute = useRouteRef(controlPlaneCockpitRouteRef);
  const [currentUserRef, setCurrentUserRef] = useState<string | null>(null);
  const {
    roles: accessibleProjects,
    loading: rbacLoading,
    error: rbacError,
    hasPermission,
    isAdmin,
    refresh: refreshRoles,
  } = useProjectRole();
  /**
   * Exact global admin: role `admin` with the wildcard project key. Fails closed —
   * false while RBAC is loading or unavailable — so the destructive selection can
   * never appear on an unresolved identity (ADR-005). The UI is not the
   * authorization boundary; the backend re-checks every call.
   */
  const isGlobalAdmin = !rbacLoading && !rbacError && isAdmin;

  /**
   * Estado único de permissão de criação, consumido por **todas** as entradas da
   * página: o botão do cabeçalho, a ação do estado vazio e o caminho delegado
   * pela prop `onCreate`. Antes, só o botão do cabeçalho era guardado e a ação
   * do estado vazio abria o modal sem condição alguma — portfólio vazio virava
   * janela fail-open.
   *
   * Fail-closed: enquanto a consulta de papéis carrega ou falha, a criação não é
   * liberada. A interface não é a fronteira de autorização, e o servidor recheca
   * toda chamada; o que se evita aqui é prometer uma capability que o servidor
   * vai negar (ADR-002).
   */
  const permissionsResolved = !rbacLoading && !rbacError;
  const canCreateActivity =
    permissionsResolved &&
    accessibleProjects.some(p =>
      hasPermission(p.projectKey, 'activity.create'),
    );
  const creationEntryState = resolveCreationEntryState({
    rbacError: Boolean(rbacError),
    rbacLoading,
    canCreateActivity,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const openCreation = onCreate ?? (() => setDialogOpen(true));
  // Ponto único de decisão: nenhum ponto de chamada decide por conta própria.
  const handleCreate = () => {
    if (!canCreateActivity) return;
    openCreation();
  };
  const [activities, setActivities] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [actionInfo, setActionInfo] = useState<string>();
  const [deletingIncomplete, setDeletingIncomplete] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deletionBatch, setDeletionBatch] =
    useState<ActivityDeletionBatchView | null>(null);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState<ActivityFilterOptions>({
    projects: [],
    repositories: [],
    creators: [],
  });
  const [projectLabels, setProjectLabels] = useState<Record<string, string>>(
    {},
  );
  const [userLabels, setUserLabels] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<ActivityFilters>({
    page: 1,
    limit: 20,
    mineOnly: true,
    workPath,
  });

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!cancelled) setCurrentUserRef(identity.userEntityRef);
      })
      .catch(() => {
        if (!cancelled) setCurrentUserRef(null);
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const getProjectLabel = useCallback(
    (projectId: string) => projectLabels[projectId] ?? projectId,
    [projectLabels],
  );
  const getUserLabel = useCallback(
    (userId: string) => userLabels[userId] ?? formatUserEntityRef(userId),
    [userLabels],
  );

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await api.listActivities(filters);
      setActivities(result.data);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  const loadProjectLabels = useCallback(async () => {
    try {
      const projects = await api.listProjects();
      const nextLabels = Object.fromEntries(
        projects.map(project => [project.id, project.name]),
      );
      setProjectLabels(nextLabels);
    } catch {
      // Keep fallback labels when project lookup fails.
    }
  }, [api]);

  useEffect(() => {
    void loadProjectLabels();
  }, [loadProjectLabels]);

  const loadFilterOptions = useCallback(async () => {
    setLoadingFilterOptions(true);
    try {
      const result = await api.getActivityFilterOptions({
        status: filters.status,
        workPath: filters.workPath,
        azureProject: filters.azureProject,
        azureRepository: filters.azureRepository,
        source: filters.source,
        issueExternalId: filters.issueExternalId,
        createdFrom: filters.createdFrom,
        createdToExclusive: filters.createdToExclusive,
        mineOnly: filters.mineOnly,
      });
      setFilterOptions({
        projects: result.projects ?? [],
        repositories: result.repositories ?? [],
        creators: result.creators ?? [],
      });
    } catch {
      // Keep existing options when filter options endpoint fails.
    } finally {
      setLoadingFilterOptions(false);
    }
  }, [
    api,
    filters.azureProject,
    filters.azureRepository,
    filters.createdFrom,
    filters.createdToExclusive,
    filters.issueExternalId,
    filters.mineOnly,
    filters.source,
    filters.status,
    filters.workPath,
  ]);

  useEffect(() => {
    void loadFilterOptions();
  }, [loadFilterOptions]);

  const catalogUserRefs = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...activities.map(activity => activity.userId),
            ...filterOptions.creators,
          ].filter(userId => Boolean(userId) && userId !== 'anonymous'),
        ),
      ),
    [activities, filterOptions.creators],
  );

  useEffect(() => {
    if (catalogUserRefs.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const loadCatalogUserLabels = async () => {
      try {
        const response = await catalogApi.getEntitiesByRefs({
          entityRefs: catalogUserRefs,
          fields: [
            'metadata.name',
            'metadata.title',
            'spec.profile.displayName',
          ],
        });

        if (cancelled) {
          return;
        }

        const nextLabels: Record<string, string> = {};
        catalogUserRefs.forEach((userRef, index) => {
          const label = getCatalogEntityDisplayName(response.items[index]);
          if (label) {
            nextLabels[userRef] = label;
          }
        });

        if (Object.keys(nextLabels).length > 0) {
          setUserLabels(prev => ({
            ...prev,
            ...nextLabels,
          }));
        }
      } catch {
        // Keep fallback labels when catalog lookup fails.
      }
    };

    void loadCatalogUserLabels();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, catalogUserRefs]);

  useEffect(() => {
    if (
      filters.azureProject &&
      !filterOptions.projects.includes(filters.azureProject)
    ) {
      setFilters(prev => ({
        ...prev,
        azureProject: undefined,
        azureRepository: undefined,
        page: 1,
      }));
      return;
    }

    if (
      filters.azureRepository &&
      !filterOptions.repositories.includes(filters.azureRepository)
    ) {
      setFilters(prev => ({
        ...prev,
        azureRepository: undefined,
        page: 1,
      }));
      return;
    }

    if (
      filters.createdBy &&
      !filterOptions.creators.includes(filters.createdBy)
    ) {
      setFilters(prev => ({
        ...prev,
        createdBy: undefined,
        page: 1,
      }));
    }
  }, [
    filterOptions.creators,
    filterOptions.projects,
    filterOptions.repositories,
    filters.azureProject,
    filters.azureRepository,
    filters.createdBy,
  ]);

  const handleFiltersChange = useCallback((patch: Partial<ActivityFilters>) => {
    setSelectedIds([]);
    setFilters(prev => ({ ...prev, ...patch, page: 1 }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setSelectedIds([]);
    setFilters(prev => ({
      page: 1,
      limit: prev.limit ?? 20,
      mineOnly: prev.mineOnly ?? true,
      workPath,
    }));
  }, [workPath]);

  const isActivityLockedForUser = useCallback(
    (activity: Activity) => {
      if (currentUserRef === null) return false;
      if (activity.userId === currentUserRef) return false;
      if (activity.status === 'completed') return false;
      if (hasPermission(activity.azureProject, 'activity.bypass_owner_lock')) {
        return false;
      }
      return true;
    },
    [currentUserRef, hasPermission],
  );

  /**
   * ADR-005: owner-wide deletion is gone. A global administrator now selects
   * explicit activities and confirms once; each item then advances on its own,
   * and a completed activity is refused rather than deleted.
   */
  // ADR-015 asks the portal for two things the delivered page lacked: follow the
  // batch by polling with backoff, and resume the query by `batch_id` after
  // navigation or reload. The id lives in sessionStorage — it is an opaque
  // correlation id, never a secret — so a refresh does not orphan a batch whose
  // items are still waiting on an agent.
  const rememberBatchId = useCallback((batchId: string | null) => {
    try {
      if (batchId) sessionStorage.setItem(DELETION_BATCH_STORAGE_KEY, batchId);
      else sessionStorage.removeItem(DELETION_BATCH_STORAGE_KEY);
    } catch {
      // Private-mode or disabled storage only costs resume, never correctness.
    }
  }, []);

  const handleRequestDeletionBatch = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setDeletingIncomplete(true);
    setError(undefined);
    setActionInfo(undefined);
    try {
      const view = await api.requestActivityDeletionBatch(
        selectedIds,
        `batch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      );
      setDeletionBatch(view);
      rememberBatchId(view.pending ? view.batchId : null);
      setSelectedIds([]);
      const deleted = view.items.filter(
        item => item.outcome === 'deleted',
      ).length;
      const pending = view.items.filter(
        item => item.outcome === 'cleanup_pending',
      ).length;
      setActionInfo(
        `${deleted} excluída(s); ${pending} aguardando o agente local.`,
      );
      await Promise.all([loadActivities(), loadFilterOptions()]);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setDeletingIncomplete(false);
    }
  }, [api, loadActivities, loadFilterOptions, rememberBatchId, selectedIds]);

  // Resume on mount: a batch left pending survives navigation and reload.
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(DELETION_BATCH_STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (!stored) return undefined;
    let active = true;
    void api
      .getActivityDeletionBatch(stored)
      .then(view => {
        if (!active) return;
        setDeletionBatch(view);
        if (!view || !view.pending) rememberBatchId(null);
      })
      .catch(() => rememberBatchId(null));
    return () => {
      active = false;
    };
  }, [api, rememberBatchId]);

  // Poll with backoff while any item is still waiting on its agent. The backend
  // advances a pending item as soon as its cleanup resolves, so polling is what
  // actually drives the batch to completion.
  useEffect(() => {
    if (!deletionBatch?.pending) return undefined;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let active = true;
    const tick = () => {
      const delay = Math.min(
        DELETION_POLL_BASE_MS * 2 ** attempt,
        DELETION_POLL_MAX_MS,
      );
      attempt += 1;
      timer = setTimeout(() => {
        void api
          .getActivityDeletionBatch(deletionBatch.batchId)
          .then(view => {
            if (!active) return;
            setDeletionBatch(view);
            if (!view || !view.pending) {
              rememberBatchId(null);
              void loadActivities();
            } else {
              tick();
            }
          })
          .catch(() => {
            if (active) tick();
          });
      }, delay);
    };
    tick();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [api, deletionBatch, loadActivities, rememberBatchId]);

  const handleOverrideCleanup = useCallback(
    async (activityId: string) => {
      setDeletingIncomplete(true);
      setError(undefined);
      try {
        await api.overrideActivityDeletionCleanup(
          activityId,
          `override-${activityId}-${Date.now()}`,
        );
        setActionInfo('Exclusão local ignorada; dados centrais removidos.');
        if (deletionBatch) {
          const refreshed = await api.getActivityDeletionBatch(
            deletionBatch.batchId,
          );
          setDeletionBatch(refreshed);
        }
        await Promise.all([loadActivities(), loadFilterOptions()]);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setDeletingIncomplete(false);
      }
    },
    [api, deletionBatch, loadActivities, loadFilterOptions],
  );

  const getPrimaryActivityRoute = useCallback(
    (activity: Activity) => {
      if (!isTerminalWorkPath(activity)) {
        return normalizeRouteRefPath(
          activityDetailRoute({ activityId: activity.id }),
        );
      }

      // Todo work path terminal roteia para /connect: a página de
      // descontinuação decide entre o redirect control-plane e o aviso
      // (o painel CLI dedicado foi descontinuado — ADR-005).
      return normalizeRouteRefPath(
        activityConnectRoute({ activityId: activity.id }),
      );
    },
    [activityConnectRoute, activityDetailRoute],
  );

  const getActivityCockpitRoute = useCallback(
    (activity: Activity) => {
      if (activity.workPath === 'control-plane' && controlPlaneCockpitRoute) {
        return normalizeRouteRefPath(
          controlPlaneCockpitRoute({ activityId: activity.id }),
        );
      }
      return normalizeRouteRefPath(
        activityMetricsRoute({ activityId: activity.id }),
      );
    },
    [activityMetricsRoute, controlPlaneCockpitRoute],
  );

  const eligibleActivityIds = useMemo(
    () =>
      activities
        .filter(activity => activity.status !== 'completed')
        .map(activity => activity.id),
    [activities],
  );
  const selectedEligibleCount = eligibleActivityIds.filter(id =>
    selectedIds.includes(id),
  ).length;
  const allEligibleSelected =
    eligibleActivityIds.length > 0 &&
    selectedEligibleCount === eligibleActivityIds.length;

  useEffect(() => {
    setSelectedIds(previous => {
      const visible = previous.filter(id => eligibleActivityIds.includes(id));
      return visible.length === previous.length ? previous : visible;
    });
  }, [eligibleActivityIds]);

  const columns: TableColumn<Activity>[] = [
    {
      title: (
        <Checkbox
          size="small"
          checked={allEligibleSelected}
          indeterminate={selectedEligibleCount > 0 && !allEligibleSelected}
          disabled={eligibleActivityIds.length === 0 || deletingIncomplete}
          inputProps={{
            'aria-label': 'Selecionar atividades elegíveis desta página',
            'aria-checked':
              selectedEligibleCount > 0 && !allEligibleSelected
                ? 'mixed'
                : allEligibleSelected,
          }}
          onClick={event => event.stopPropagation()}
          onChange={event =>
            setSelectedIds(event.target.checked ? eligibleActivityIds : [])
          }
        />
      ),
      field: 'selection' as any,
      width: '48px',
      sorting: false,
      // Visible only to a global administrator, and never selectable for a
      // completed activity: those are permanently protected (ADR-005).
      hidden: !isGlobalAdmin,
      render: row => {
        const protectedRow = row.status === 'completed';
        const checkbox = (
          <Checkbox
            size="small"
            checked={selectedIds.includes(row.id)}
            disabled={protectedRow || deletingIncomplete}
            inputProps={{
              'aria-label': protectedRow
                ? `${row.title}: atividade concluída, protegida contra exclusão`
                : `Selecionar ${row.title} para exclusão`,
            }}
            onClick={event => event.stopPropagation()}
            onChange={event =>
              setSelectedIds(previous =>
                event.target.checked
                  ? [...new Set([...previous, row.id])]
                  : previous.filter(id => id !== row.id),
              )
            }
          />
        );
        return protectedRow ? (
          <Tooltip
            title="Atividades concluídas são permanentemente protegidas contra exclusão."
            arrow
          >
            <span>{checkbox}</span>
          </Tooltip>
        ) : (
          checkbox
        );
      },
    },
    {
      title: 'Título',
      field: 'title',
      highlight: true,
      width: '220px',
      render: row => (
        <ActivityTitleCell
          activity={row}
          locked={isActivityLockedForUser(row)}
          route={getPrimaryActivityRoute(row)}
        />
      ),
    },
    {
      title: 'Issue',
      field: 'azureWorkItem' as any,
      width: '68px',
      render: row => {
        if (row.azureWorkItem) {
          return (
            <Tooltip
              title={`${row.azureWorkItem.type}: ${row.azureWorkItem.title}`}
              arrow
            >
              <a
                href={row.azureWorkItem.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  fontWeight: 600,
                  color: '#FA4616',
                  textDecoration: 'none',
                }}
              >
                #{row.azureWorkItem.id}
              </a>
            </Tooltip>
          );
        }
        if (row.issue) {
          return (
            <Tooltip
              title={`${row.issue.type ? `${row.issue.type}: ` : ''}${
                row.issue.title
              }`}
              arrow
            >
              <a
                href={row.issue.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  fontWeight: 600,
                  color: '#FA4616',
                  textDecoration: 'none',
                }}
              >
                #{row.issue.externalId}
              </a>
            </Tooltip>
          );
        }
        return <span style={{ color: '#ccc' }}>—</span>;
      },
    },
    {
      // ADR-001: the activity cycle is its own dimension, named as such, and read
      // from the canonical projection when the row carries one.
      title: 'Status',
      field: 'status',
      width: '95px',
      render: row =>
        row.controlPlane ? (
          <ControlPlaneCycleCell
            projection={row.controlPlane}
            fallbackStatus={row.status}
          />
        ) : (
          <StatusIndicator
            status={row.status}
            errorMessage={row.errorMessage}
          />
        ),
    },
    {
      title: 'Via de Trabalho',
      field: 'workPath',
      width: '108px',
      render: row => <WorkPathChip activity={row} />,
    },
    {
      title: 'Fase Atual',
      field: 'currentPhase',
      width: '90px',
      render: row => <PhaseChip phase={row.currentPhase} />,
    },
    {
      title: 'Destino',
      field: 'azureProject',
      width: '170px',
      render: row => (
        <DestinationCell
          activity={row}
          projectLabel={getProjectLabel(row.azureProject)}
        />
      ),
    },
    {
      title: 'Criação',
      field: 'createdAt',
      width: '145px',
      render: row => (
        <CreationCell activity={row} userLabel={getUserLabel(row.userId)} />
      ),
    },
    {
      title: 'Cockpit',
      field: 'id',
      sorting: false,
      width: '72px',
      render: row => <Link to={getActivityCockpitRoute(row)}>Cockpit</Link>,
    },
  ];

  if (error) {
    return (
      <Page themeId="tool">
        <Header title={portfolioTitle} subtitle={portfolioSubtitle}>
          {showWorkerStatus && <WorkerStatusIndicator />}
        </Header>
        <Content>
          <EmptyState
            title="Erro ao carregar atividades"
            description={error.message}
            missing="info"
            action={
              <Button
                variant="contained"
                color="primary"
                onClick={loadActivities}
              >
                Tentar novamente
              </Button>
            }
          />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title={portfolioTitle} subtitle={portfolioSubtitle}>
        {showWorkerStatus && <WorkerStatusIndicator />}
      </Header>
      <Content>
        <ContentHeader title="Atividades">
          <SupportButton>{supportText}</SupportButton>
          <CreationEntry
            state={creationEntryState}
            onCreate={handleCreate}
            onRetry={refreshRoles}
          />
        </ContentHeader>

        {!onCreate && (
          <ActivityJourneyLauncher
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
          />
        )}

        <ActivityFiltersPanel
          filters={filters}
          options={filterOptions}
          loadingOptions={loadingFilterOptions}
          fixedWorkPath={workPath}
          getProjectLabel={getProjectLabel}
          getCreatorLabel={getUserLabel}
          onFiltersChange={handleFiltersChange}
          onReset={handleResetFilters}
          resetBusy={deletingIncomplete}
          administrativeActions={
            <AdminDeletionPanel
              isGlobalAdmin={isGlobalAdmin}
              selectedIds={selectedIds}
              selectedTargets={selectedIds.map(id => {
                const row = activities.find(activity => activity.id === id);
                return {
                  id,
                  title: row?.title ?? id,
                  repoName: row?.repoName ?? row?.azureRepository ?? null,
                };
              })}
              busy={loading || deletingIncomplete}
              batch={deletionBatch}
              onRequestBatch={() => void handleRequestDeletionBatch()}
              onOverride={activityId => void handleOverrideCleanup(activityId)}
              onClearSelection={() => setSelectedIds([])}
            />
          }
        />

        {actionInfo && (
          <Typography variant="body2" className={classes.actionInfo}>
            {actionInfo}
          </Typography>
        )}

        {loading && (
          <>
            <Progress />
            <div
              className={classes.loadingSkeleton}
              aria-label="Carregando atividades"
            >
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
            </div>
          </>
        )}

        {!loading && activities.length === 0 && !error && (
          <EmptyState
            title="Nenhuma atividade encontrada"
            description={emptyDescription}
            missing="content"
            action={
              <CreationEntry
                state={creationEntryState}
                onCreate={handleCreate}
                onRetry={refreshRoles}
              />
            }
          />
        )}

        {!loading && activities.length > 0 && (
          <section
            className={classes.tableContainer}
            aria-label="Tabela de atividades"
          >
            <Table
              title={`${total} atividade${total !== 1 ? 's' : ''}`}
              columns={columns}
              data={activities}
              options={{
                paging: true,
                pageSize: filters.limit ?? 20,
                search: false,
                padding: 'dense',
                tableLayout: 'fixed',
              }}
              page={(filters.page ?? 1) - 1}
              totalCount={total}
              onPageChange={page => {
                setSelectedIds([]);
                setFilters(prev => ({ ...prev, page: page + 1 }));
              }}
              onRowsPerPageChange={pageSize => {
                setSelectedIds([]);
                setFilters(prev => ({ ...prev, limit: pageSize, page: 1 }));
              }}
            />
          </section>
        )}
      </Content>
    </Page>
  );
};
